import Choices from "choices.js";

function isUsableSelect(selectEl) {
  return (
    selectEl instanceof HTMLSelectElement &&
    !selectEl.disabled &&
    !selectEl.hasAttribute("data-sas-searchable-select-initialized")
  );
}

function destroyChoices(selectEl) {
  const inst = selectEl && selectEl.__sasChoices;
  if (inst && typeof inst.destroy === "function") {
    try {
      inst.destroy();
    } catch (e) {
      // ignore
    }
  }
  if (selectEl) {
    delete selectEl.__sasChoices;
    selectEl.removeAttribute("data-sas-searchable-select-initialized");
  }
}

function createChoices(selectEl) {
  const placeholder =
    selectEl.getAttribute("data-sas-select-placeholder") ||
    selectEl.getAttribute("placeholder") ||
    "Search…";

  // Existing-options-only: Choices does NOT allow arbitrary values unless addItems is true.
  // Keep sorting off so server-defined order (often meaningful) remains stable.
  // Also avoid injecting HTML into labels for safety.
  //
  // Fuse.js (inside Choices) defaults: threshold 0.6 is very fuzzy — many weak matches.
  // Tighter fuseOptions: better for codes/emp ids (often at end of "Name (id)") and fewer false positives.
  const inst = new Choices(selectEl, {
    searchEnabled: true,
    searchResultLimit: 50,
    searchFloor: 1,
    shouldSort: false,
    allowHTML: false,
    itemSelectText: "",
    placeholderValue: placeholder,
    searchPlaceholderValue: placeholder,
    addItems: false,
    duplicateItemsAllowed: false,
    removeItemButton: false,
    position: "bottom",
    fuseOptions: {
      includeScore: true,
      threshold: 0.32,
      ignoreLocation: true,
      minMatchCharLength: 2,
    },
  });

  selectEl.__sasChoices = inst;
  selectEl.setAttribute("data-sas-searchable-select-initialized", "1");
  return inst;
}

export function refreshSearchableSelect(selectEl) {
  if (!(selectEl instanceof HTMLSelectElement)) return;
  // Only refresh enhanced selects that opted-in.
  if (!selectEl.hasAttribute("data-sas-searchable-select")) return;

  // Preserve current value through re-init.
  const currentValue = selectEl.value;
  destroyChoices(selectEl);

  const force = selectEl.getAttribute("data-sas-searchable-select") === "force";
  const optionCount = selectEl.querySelectorAll("option").length;
  if (!force && optionCount < 12) return;

  try {
    createChoices(selectEl);
    if (currentValue) selectEl.value = currentValue;
  } catch (e) {
    // Fail open to native select
    destroyChoices(selectEl);
  }
}

export default function initSearchableSelects(root = document) {
  if (!root || typeof root.querySelectorAll !== "function") return;

  const selects = root.querySelectorAll("select[data-sas-searchable-select]");
  selects.forEach((selectEl) => {
    if (!isUsableSelect(selectEl)) return;

    // Only enhance "long" selects by default, unless forced.
    const force = selectEl.getAttribute("data-sas-searchable-select") === "force";
    const optionCount = selectEl.querySelectorAll("option").length;
    if (!force && optionCount < 12) return;

    try {
      createChoices(selectEl);
    } catch (e) {
      // Fail open to native <select> if enhancement crashes for any reason.
      destroyChoices(selectEl);
    }
  });

  // Allow feature scripts to signal that options changed and UI must refresh.
  // Usage: selectEl.dispatchEvent(new CustomEvent('sas:searchable-select-refresh'))
  root.addEventListener(
    "sas:searchable-select-refresh",
    (e) => {
      const t = e && e.target;
      if (t instanceof HTMLSelectElement) refreshSearchableSelect(t);
    },
    true
  );
}

